/**
 * `pis` — "pages in session", a client-side page-view counter sent to GAM as an
 * ad-targeting key. Ported from legacy (`srcLegacy/client/utils/browserCookieUtils.ts`).
 *
 * Nothing reads it server-side, in this app or in hotpads-api. It stays a cookie
 * rather than localStorage so the count carries across the legacy and Next
 * frontends while both serve hotpads.com, and so it expires with the browser
 * session for free.
 *
 * Attributes match legacy deliberately: universal-cookie forwards its empty
 * options straight to `cookie.serialize`, so legacy writes no Path and no
 * expiry. The browser then scopes the cookie to the current document's
 * directory — the count restarts as a visitor moves between path prefixes.
 * Diverging here would leave two `pis` cookies at different paths, and the
 * more specific one shadows the other.
 */

import { parseCookieHeader } from '@/src/lib/cookies/cookieParser';

const PIS_COOKIE_NAME = 'pis';
const EXPIRED_COOKIE_DATE = 'Thu, 01 Jan 1970 00:00:01 GMT';

export function getPis(): string | undefined {
  if (typeof document === 'undefined') return undefined;

  const value = parseCookieHeader(document.cookie)[PIS_COOKIE_NAME];
  return value && value.length > 0 ? value : undefined;
}

export function incrementPis(): void {
  if (typeof document === 'undefined') return;

  const current = Number(getPis() ?? 0);
  // Legacy would write `NaN` back out for a corrupted value; restart instead.
  const next = Number.isFinite(current) ? current + 1 : 1;
  document.cookie = `${PIS_COOKIE_NAME}=${next}`;
}

export function clearPis(): void {
  if (typeof document === 'undefined') return;

  document.cookie = `${PIS_COOKIE_NAME}=; expires=${EXPIRED_COOKIE_DATE}; max-age=0`;
}
